import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Form,
  FormField,
  Heading,
  TextInput,
  Spinner,
} from "grommet";
import ApiClient from "../api/api-client";
import useUserDetails from "../hooks/useUserDetails";
import { useTypingHeadlines } from "use-typing-headlines";
import { CONSTANT } from "./Constants";
import "../../src/entryLabel.css";
import { extractDate } from "../hooks/extractDate";
import { formatDate } from "../hooks/formateDate";
import { arrayOfEntriesToObject } from "../hooks/arrayToObject";
import { Checkmark, Send } from "grommet-icons";

function CreateEntry({ selectedDate, openUserPortal }) {
  const today = extractDate(new Date().toISOString());
  const selectedDateOnly = extractDate(selectedDate);
  const { user } = useAuthenticator();
  const { userAttributes } = useUserDetails(user);
  const [entries, setEntries] = useState(undefined);
  const [entry, setEntry] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [buttonState, setButtonState] = useState("idle");

  useEffect(() => {
    setError(undefined);
    setLoading(true);
    setEntry(undefined);

    if (!user) {
      setEntries(arrayOfEntriesToObject());
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    ApiClient.getEntryForDate(new Date(selectedDate), controller.signal)
      .then((resp) => {
        if (resp.status === 404) {
          return;
        }

        // allow 204
        if (resp.status === 204) {
          setEntries(arrayOfEntriesToObject());
          setLoading(false);
          return;
        }

        if (!resp.ok) {
          return Promise.reject(new Error(resp.statusText));
        }

        return resp.json();
      })
      .then((entry) => {
        if (!entry && entries) {
          setLoading(false);
          return;
        }

        if (!entry) {
          setEntries(new Array(5).fill(""));
          setLoading(false);
          return;
        }

        setLoading(false);
        setEntry(entry);
        setEntries(arrayOfEntriesToObject(entry["journalEntries"]));
      })
      .catch((error) => {
        setError(
          "Failed to check whether you already have an entry for today. Please reload the page to try again",
        );
      });

    return () => controller.abort();
  }, [selectedDate, user]);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // ---------- Handle Submit ----------
  const handleSubmit = ({ value }) => {
    setButtonState("loading");
    const submitPromise = entry
      ? ApiClient.updateEntryById(entry.id, Object.values(entries))
      : ApiClient.submitEntry(Object.values(value)).then((resp) => resp.json());

    Promise.all([submitPromise, sleep(500)])
      .then(([result]) => {
        if (!entry) setEntry(result);
        setButtonState("success");
        setTimeout(() => setButtonState("idle"), 1800);
      })
      .catch(() => {
        setError("Failed to submit/update entry. Please reload and try again.");
        setButtonState("idle");
      });
  };

  const buttonIcon =
    buttonState === "loading" ? (
      <Spinner size="small" />
    ) : buttonState === "success" ? (
      <Checkmark color="status-ok" />
    ) : buttonState === "idle" && !entry ? (
      <Send />
    ) : null;

  const selectedDateIsToday =
    new Date(selectedDate).setHours(0, 0, 0, 0) ===
    new Date().setHours(0, 0, 0, 0);

  const headingElement = userAttributes ? (
    <>
      {CONSTANT.HEADING_TEXT},{" "}
      <span className="username">{userAttributes.given_name}</span>
    </>
  ) : (
    CONSTANT.HEADING_TEXT_NO_USER
  );

  const subHeadingElement =
    userAttributes && selectedDateOnly !== today ? (
      <>{selectedDateOnly ? formatDate(selectedDateOnly) : ""}</>
    ) : (
      CONSTANT.SUBHEADING_TEXT
    );

  const [subHeadingElementDynamic] = useTypingHeadlines(
    [
      CONSTANT.SUBHEADING_TEXT,
      "Something you're proud of...",
      "Anything you're grateful for...",
      "Appreciating even the small things counts...",
    ],
    { headlineInterval: 12000, letterInterval: 35 },
  );

  if (error) {
    return (
      <Box pad="small">
        <Card pad="small" gap="medium" elevation="small">
          <Heading textAlign="center" align="center" level="3">
            {error}
          </Heading>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Heading
          className="welcomeHeader"
          textAlign="center"
          align="center"
          margin={{ top: "none", bottom: "none" }}
          level="2"
        >
          {headingElement}
        </Heading>
        <Heading
          className="subtitle"
          textAlign="center"
          align="center"
          level="3"
          margin={{ top: "small", bottom: "medium" }}
        >
          {subHeadingElement}
        </Heading>
        <Box align="center">
          <Spinner size="medium"></Spinner>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box align="center">
        <Heading
          className="welcomeHeader"
          textAlign="center"
          align="center"
          margin={{ top: "none", bottom: "none" }}
          level="2"
        >
          {headingElement}
        </Heading>
        <Heading
          className="subtitle"
          textAlign="center"
          align="center"
          level="3"
          margin={{ top: "small", bottom: "medium" }}
          style={{ minHeight: "1.3em" }} // Adjustment to retain space with typingHeadlines
        >
          {subHeadingElementDynamic}
        </Heading>

        <Box width="medium" align="center" justify="center">
          <Card
            className="card"
            pad="small"
            gap="medium"
            background="light-1"
            elevation="none"
          >
            <Form
              className="entryForm"
              value={entries} // Pass the entries state to the Form value prop
              onChange={(nextValue) => setEntries(nextValue)} // Update entries state on form change
              onSubmit={handleSubmit}
            >
              {new Array(5).fill().map((_, index) => (
                <FormField
                  key={index}
                  name={index.toString()}
                  label={`${index + 1}`}
                  className="entryLabel"
                >
                  <TextInput
                    className="textInput"
                    name={index.toString()}
                    placeholder={""}
                    value={entries[index.toString()]}
                    readOnly={!selectedDateIsToday}
                    // required
                    minLength={1}
                  />
                </FormField>
              ))}
              <Box className="submit">
                <Box className="submitContainer">
                  <Button
                    type="submit"
                    label={entry ? "Update" : "Create"}
                    disabled={buttonState === "loading"}
                    icon={buttonIcon}
                    reverse
                    gap="xsmall"
                    className={`submitButton${
                      buttonIcon ? " submitButton--withIcon" : ""
                    }`}
                  />
                </Box>
              </Box>
            </Form>
          </Card>
        </Box>
      </Box>
    </>
  );
}

export default CreateEntry;
